import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import * as QRCode from 'qrcode';

interface StoryCardGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  professional: {
    id: number;
    nombre: string;
    apellido: string;
    especialidad?: string;
    professional_type?: string;
    business_name?: string;
    picture?: string;
    slug?: string;
  };
}

type CardStyle = 'dark' | 'gradient' | 'clean' | 'minimal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const StoryCardGenerator: React.FC<StoryCardGeneratorProps> = ({ isOpen, onClose, professional }) => {
  const [selectedStyle, setSelectedStyle] = useState<CardStyle>('gradient');
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Prevent background scroll when modal is open (simple approach)
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      return () => {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  const shareUrl = `${API_URL}/p/${professional.slug || professional.id}`;
  const displayUrl = `consultarapp.com/p/${professional.slug || professional.id}`;
  const profName = professional.business_name || `${professional.nombre} ${professional.apellido}`;
  const specialty = professional.especialidad || '';

  const generateCard = useCallback(async (style: CardStyle): Promise<string> => {
    const canvas = canvasRef.current;
    if (!canvas) throw new Error('Canvas not available');

    // Story size: 1080x1920 (9:16)
    const W = 1080;
    const H = 1920;
    canvas.width = W;
    canvas.height = H;

    const ctx = canvas.getContext('2d')!;

    // Backgrounds
    switch (style) {
      case 'dark':
        const darkGrad = ctx.createLinearGradient(0, 0, W, H);
        darkGrad.addColorStop(0, '#0f172a');
        darkGrad.addColorStop(0.5, '#1e293b');
        darkGrad.addColorStop(1, '#0f172a');
        ctx.fillStyle = darkGrad;
        ctx.fillRect(0, 0, W, H);
        // Decorative circles
        ctx.globalAlpha = 0.08;
        ctx.fillStyle = '#3b82f6';
        ctx.beginPath(); ctx.arc(200, 400, 300, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#10b981';
        ctx.beginPath(); ctx.arc(880, 1400, 350, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
        break;

      case 'gradient':
        const grad = ctx.createLinearGradient(0, 0, W, H);
        grad.addColorStop(0, '#3b82f6');
        grad.addColorStop(0.4, '#06b6d4');
        grad.addColorStop(1, '#10b981');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, W, H);
        // Pattern overlay
        ctx.globalAlpha = 0.06;
        for (let i = 0; i < 20; i++) {
          ctx.fillStyle = '#fff';
          ctx.beginPath();
          ctx.arc(Math.random() * W, Math.random() * H, Math.random() * 150 + 50, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        break;

      case 'clean':
        ctx.fillStyle = '#f8fafc';
        ctx.fillRect(0, 0, W, H);
        // Top accent bar
        const accentGrad = ctx.createLinearGradient(0, 0, W, 0);
        accentGrad.addColorStop(0, '#3b82f6');
        accentGrad.addColorStop(1, '#10b981');
        ctx.fillStyle = accentGrad;
        roundRect(ctx, 80, 120, W - 160, 8, 4);
        ctx.fill();
        break;

      case 'minimal':
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, W, H);
        // Subtle border
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 4;
        roundRect(ctx, 60, 60, W - 120, H - 120, 40);
        ctx.stroke();
        break;
    }

    const isDark = style === 'dark' || style === 'gradient';
    const textColor = isDark ? '#ffffff' : '#1e293b';
    const subTextColor = isDark ? 'rgba(255,255,255,0.7)' : '#64748b';

    // consultAR logo text at top
    ctx.fillStyle = isDark ? 'rgba(255,255,255,0.5)' : '#94a3b8';
    ctx.font = 'bold 36px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('consultAR', W / 2, 200);

    // Profile picture
    const picY = 380;
    const picSize = 280;

    if (professional.picture) {
      try {
        const img = await loadImage(professional.picture);
        // Circular clip
        ctx.save();
        ctx.beginPath();
        ctx.arc(W / 2, picY + picSize / 2, picSize / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(img, W / 2 - picSize / 2, picY, picSize, picSize);
        ctx.restore();

        // Border
        ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(59,130,246,0.3)';
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.arc(W / 2, picY + picSize / 2, picSize / 2 + 3, 0, Math.PI * 2);
        ctx.stroke();
      } catch (e) {
        // Fallback: initials circle
        drawInitialsCircle(ctx, W / 2, picY + picSize / 2, picSize / 2, professional.nombre, professional.apellido);
      }
    } else {
      drawInitialsCircle(ctx, W / 2, picY + picSize / 2, picSize / 2, professional.nombre, professional.apellido);
    }

    // Name
    ctx.fillStyle = textColor;
    ctx.font = 'bold 72px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'center';

    // Handle long names
    const nameLines = wrapText(ctx, profName, W - 160);
    let nameY = picY + picSize + 100;
    nameLines.forEach((line) => {
      ctx.fillText(line, W / 2, nameY);
      nameY += 85;
    });

    // Specialty
    if (specialty) {
      ctx.fillStyle = subTextColor;
      ctx.font = '500 48px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText(specialty, W / 2, nameY + 20);
      nameY += 60;
    }

    // Divider line
    const divY = nameY + 60;
    const lineGrad = ctx.createLinearGradient(W / 2 - 150, 0, W / 2 + 150, 0);
    lineGrad.addColorStop(0, 'transparent');
    lineGrad.addColorStop(0.5, isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.15)');
    lineGrad.addColorStop(1, 'transparent');
    ctx.fillStyle = lineGrad;
    ctx.fillRect(W / 2 - 150, divY, 300, 2);

    // "Reserva tu turno" text
    ctx.fillStyle = textColor;
    ctx.font = 'bold 52px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillText('Reserva tu turno', W / 2, divY + 90);

    // QR Code - siempre con fondo blanco para visibilidad
    const qrY = divY + 140;
    const qrSize = 320;
    const qrPadding = 30;
    try {
      const qrDataUrl = await QRCode.toDataURL(shareUrl, {
        width: qrSize,
        margin: 2,
        color: {
          dark: '#1e293b',
          light: '#ffffff',
        },
      });
      const qrImg = await loadImage(qrDataUrl);

      // QR background card (siempre blanco para contraste)
      ctx.fillStyle = '#ffffff';
      roundRect(ctx, W / 2 - qrSize / 2 - qrPadding, qrY - qrPadding, qrSize + qrPadding * 2, qrSize + qrPadding * 2, 24);
      ctx.fill();

      // Sombra sutil
      ctx.shadowColor = 'rgba(0,0,0,0.15)';
      ctx.shadowBlur = 30;
      ctx.shadowOffsetY = 10;
      roundRect(ctx, W / 2 - qrSize / 2 - qrPadding, qrY - qrPadding, qrSize + qrPadding * 2, qrSize + qrPadding * 2, 24);
      ctx.fill();
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;

      ctx.drawImage(qrImg, W / 2 - qrSize / 2, qrY, qrSize, qrSize);
    } catch (e) {
      console.error('QR generation error:', e);
      // Fallback: dibujar un placeholder de QR
      ctx.fillStyle = '#ffffff';
      roundRect(ctx, W / 2 - qrSize / 2 - qrPadding, qrY - qrPadding, qrSize + qrPadding * 2, qrSize + qrPadding * 2, 24);
      ctx.fill();
      ctx.fillStyle = '#94a3b8';
      ctx.font = 'bold 40px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText('QR', W / 2, qrY + qrSize / 2 + 15);
    }

    // URL below QR - más visible
    ctx.fillStyle = textColor;
    ctx.font = 'bold 36px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillText(displayUrl, W / 2, qrY + qrSize + qrPadding + 60);

    // CTA at bottom
    const ctaY = H - 180;
    // Button background
    ctx.fillStyle = isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)';
    roundRect(ctx, W / 2 - 280, ctaY - 35, 560, 70, 35);
    ctx.fill();

    ctx.fillStyle = textColor;
    ctx.font = 'bold 34px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillText('Escaneá el QR o visitá el link', W / 2, ctaY + 8);

    return canvas.toDataURL('image/png', 1.0);
  }, [professional, profName, specialty, shareUrl, displayUrl]);

  const handleGenerate = async (style: CardStyle) => {
    setSelectedStyle(style);
    setGenerating(true);
    try {
      const dataUrl = await generateCard(style);
      setPreviewUrl(dataUrl);
    } catch (e) {
      console.error('Error generating card:', e);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!previewUrl) return;
    const link = document.createElement('a');
    link.download = `consultAR-${professional.slug || professional.id}-story.png`;
    link.href = previewUrl;
    link.click();
  };

  const handleShareStory = async () => {
    if (!previewUrl) return;

    try {
      const response = await fetch(previewUrl);
      const blob = await response.blob();
      const file = new File([blob], `consultAR-story.png`, { type: 'image/png' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `${profName} - consultAR`,
          text: `Reserva tu turno con ${profName}`,
        });
      } else {
        // Fallback: download
        handleDownload();
      }
    } catch (e) {
      console.error('Share error:', e);
      handleDownload();
    }
  };

  // Generate on open
  React.useEffect(() => {
    if (isOpen && !previewUrl) {
      handleGenerate('gradient');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const styles: { key: CardStyle; label: string; colors: string }[] = [
    { key: 'gradient', label: 'Gradiente', colors: 'from-blue-500 to-green-500' },
    { key: 'dark', label: 'Oscuro', colors: 'from-slate-800 to-slate-900' },
    { key: 'clean', label: 'Claro', colors: 'from-gray-100 to-white' },
    { key: 'minimal', label: 'Minimal', colors: 'from-white to-gray-50' },
  ];

  return ReactDOM.createPortal(
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        overscrollBehavior: 'contain',
      }}
    >
      <div style={{
        background: 'white',
        width: '100%',
        maxWidth: '32rem',
        maxHeight: '92vh',
        borderTopLeftRadius: '1rem',
        borderTopRightRadius: '1rem',
        display: 'flex',
        flexDirection: 'column' as const,
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
      }} className="sm:rounded-2xl sm:mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Crear Story Card</h3>
            <p className="text-xs sm:text-sm text-gray-500">Para compartir en IG y WhatsApp Stories</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Style selector */}
        <div className="px-5 py-3 flex gap-2 overflow-x-auto">
          {styles.map((s) => (
            <button
              key={s.key}
              onClick={() => handleGenerate(s.key)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedStyle === s.key
                  ? 'bg-gray-900 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className={`inline-block w-3 h-3 rounded-full bg-gradient-to-r ${s.colors} mr-2`} />
              {s.label}
            </button>
          ))}
        </div>

        {/* Preview */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-3 min-h-0">
          <div className="bg-gray-100 rounded-2xl p-2 sm:p-3 flex items-center justify-center">
            {generating ? (
              <div className="h-[300px] sm:h-[380px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
              </div>
            ) : previewUrl ? (
              <img
                src={previewUrl}
                alt="Story preview"
                className="max-h-[300px] sm:max-h-[380px] rounded-xl shadow-lg"
              />
            ) : (
              <div className="h-[300px] sm:h-[380px] flex items-center justify-center text-gray-400">
                Generando preview...
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 sm:p-5 border-t border-gray-100 space-y-2 sm:space-y-3 flex-shrink-0 pb-safe">
          <button
            onClick={handleShareStory}
            disabled={!previewUrl || generating}
            className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white py-3 sm:py-3.5 rounded-xl font-semibold text-sm sm:text-base hover:shadow-lg transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            Compartir en Stories
          </button>
          <button
            onClick={handleDownload}
            disabled={!previewUrl || generating}
            className="w-full bg-gray-100 text-gray-700 py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base hover:bg-gray-200 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Descargar imagen
          </button>
        </div>
      </div>

      {/* Hidden canvas */}
      <canvas ref={canvasRef} className="hidden" />
    </div>,
    document.body
  );
};

// =====================================================
// Helper functions
// =====================================================

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawInitialsCircle(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, nombre: string, apellido: string) {
  const grad = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
  grad.addColorStop(0, '#3b82f6');
  grad.addColorStop(1, '#10b981');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  const initials = `${(nombre || '')[0] || ''}${(apellido || '')[0] || ''}`.toUpperCase();
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${r * 0.8}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initials, cx, cy);
  ctx.textBaseline = 'alphabetic';
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  words.forEach(word => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });
  if (currentLine) lines.push(currentLine);
  return lines;
}

export default StoryCardGenerator;
